import * as React from 'react';
import { useMemo, useState, useCallback, useRef } from 'react';
import { Box, Typography, Snackbar, Alert } from '@mui/material';
import { withTranslation } from 'react-i18next';
import axios from 'axios';
import { can } from '../../utils/access';
import { useUserStore } from '../../store';
import useCalendarState from './hooks/useCalendarState';
import { isToday, parseISO, format, setHour, dayGridHours, eventRowsPeriod, parseTimeToHour } from './hooks/dateUtils';
import {
  HOUR_HEIGHT,
  TIME_COLUMN_WIDTH,
  UI_COLORS,
  BOOKING_STATUS_CONFIG,
  BREAK_PERIOD_CONFIG,
  SNAPS_PER_HOUR,
  calculateEventOverlaps,
} from './calendarConstants';
import { applyEventFilters } from './hooks/eventFilters';
import MaintenanceEvent from './MaintenanceEvent';
import UnavailabilityEvent from './UnavailabilityEvent';
import CalendarNoteEvent from './CalendarNoteEvent';
import AllViewEvent from './AllViewEvent';
import DragSelection from './DragSelection';
import NonWorkingHoursOverlay from './NonWorkingHoursOverlay';
import { hoursForDate } from '../../utils/weekdayHours';
import EmployeeDetailsDialog from '../employees/EmployeeDetailsDialog';

const MIN_RESOURCE_COLUMN_WIDTH = 140;

function CalendarDayGrid({ t, isShortScreen = false }) {
  const canSchedule = useUserStore((s) => can(s.currentOrganization, 'work_orders.schedule'));
  const {
    currentView,
    employees,
    serviceBays,
    events,
    unavailabilities,
    calendarNotes,
    bookingEvents,
    breakPeriodEvents,
    selectedDay,
    currentDate,
    workSchedule,
    draggedMaintenance,
    setDraggedMaintenance,
    addEvent,
    removeFromUnscheduled,
    updateEmployee,
    filters,
  } = useCalendarState();

  // Employee details dialog state
  const [employeeDialogOpen, setEmployeeDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const [dropHighlight, setDropHighlight] = useState(null);
  const [dropError, setDropError] = useState(null);
  const gridRef = useRef(null);
  const headerRef = useRef(null);
  const scrollBodyRef = useRef(null);

  const date = selectedDay || currentDate;
  const dayDateStr = format(date, 'yyyy-MM-dd');
  const isTodayView = isToday(date);

  // In day mode 'all' view: show employees as individual columns (the key value of day view)
  const isAllView = currentView === 'all';

  // Apply resource filters
  const filteredEmployees = useMemo(() => {
    if (filters.employees.length === 0) return employees;
    return employees.filter((emp) => filters.employees.includes(emp.id));
  }, [employees, filters.employees]);

  const filteredServiceBays = useMemo(() => {
    if (filters.serviceBays.length === 0) return serviceBays;
    return serviceBays.filter((bay) => filters.serviceBays.includes(bay.id));
  }, [serviceBays, filters.serviceBays]);

  // Filter events
  const filteredEvents = useMemo(
    () => applyEventFilters(events, filters),
    [events, filters]
  );

  // Resources = columns in day view
  // 'all' view = single merged column (same as week view), employee/bay = individual columns
  const resources = useMemo(() => {
    if (isAllView) {
      return [{ id: 'all', name: t('All maintenances'), initials: 'ALL' }];
    }
    return currentView === 'employee' ? filteredEmployees : filteredServiceBays;
  }, [isAllView, currentView, filteredEmployees, filteredServiceBays, t]);

  // Rows: this day's work hours (not the whole week's, like the week grid has to), widened to fit
  // the day's bookings and, in the employee view, the shown mechanics' own hours that day
  const { startHour, endHour, hours, rowHeight } = useMemo(() => {
    const periods = filteredEvents.map((event) => eventRowsPeriod(event, dayDateStr));
    if (currentView === 'employee') {
      filteredEmployees.forEach((emp) => {
        const own = emp.work_hours ? hoursForDate(emp.work_hours, date) : null;
        if (own) periods.push({ start: parseTimeToHour(own.start), end: parseTimeToHour(own.end) });
      });
    }
    const { startHour: startInt, endHour: endInt } = dayGridHours({ workSchedule, date, periods, filters });
    const hoursArray = Array.from({ length: endInt - startInt }, (_, i) => startInt + i);
    return {
      startHour: startInt,
      endHour: endInt,
      hours: hoursArray,
      rowHeight: hoursArray.length * HOUR_HEIGHT,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.visibleStartHour, filters.visibleEndHour, workSchedule, dayDateStr, filteredEvents, filteredEmployees, currentView]);

  // Build events map keyed by resourceId for the single day
  const { eventsMap, unavailabilitiesMap } = useMemo(() => {
    const evMap = {};
    const unavailMap = {};

    filteredEvents.forEach((event) => {
      // Check if event spans this day
      const spansDay = event.local_start_date && event.local_end_date
        ? (dayDateStr >= event.local_start_date && dayDateStr <= event.local_end_date)
        : format(parseISO(event.start_time), 'yyyy-MM-dd') === dayDateStr;

      if (!spansDay) return;

      const isMultiDay = event.local_start_date && event.local_end_date &&
        event.local_start_date !== event.local_end_date;

      // In 'all' view, all events go into a single 'all' bucket
      const resourceIds = isAllView
        ? ['all']
        : currentView === 'employee'
          ? (event.employee_ids || [])
          : (event.service_bay_id ? [event.service_bay_id] : []);

      resourceIds.forEach((resourceId) => {
        if (!evMap[resourceId]) evMap[resourceId] = [];

        if (isMultiDay) {
          const startsToday = event.local_start_date === dayDateStr;
          const endsToday = event.local_end_date === dayDateStr;
          const clampedEvent = {
            ...event,
            _clampedStartHour: startsToday ? Math.max(startHour, event.local_start_hour) : startHour,
            _clampedEndHour: endsToday ? Math.min(endHour, event.local_end_hour) : endHour,
            _isMultiDay: true,
            _dayPosition: startsToday ? 'first' : (endsToday ? 'last' : 'middle'),
          };
          if (clampedEvent._clampedEndHour > clampedEvent._clampedStartHour) {
            evMap[resourceId].push(clampedEvent);
          }
        } else {
          evMap[resourceId].push(event);
        }
      });
    });

    // Build unavailabilities map
    if (isAllView) {
      // In 'all' view, all unavailabilities go into 'all' bucket
      unavailabilities.forEach((u) => {
        if (u.local_start_date <= dayDateStr && u.local_end_date >= dayDateStr) {
          if (!unavailMap['all']) unavailMap['all'] = [];
          const startsToday = u.local_start_date === dayDateStr;
          const endsToday = u.local_end_date === dayDateStr;
          const isMultiDay = u.local_start_date !== u.local_end_date;
          const clamped = {
            ...u,
            _clampedStartHour: startsToday ? Math.max(startHour, u.local_start_hour) : startHour,
            _clampedEndHour: endsToday ? Math.min(endHour, u.local_end_hour) : endHour,
            _isMultiDay: isMultiDay,
            _dayPosition: isMultiDay ? (startsToday ? 'first' : (endsToday ? 'last' : 'middle')) : undefined,
          };
          if (clamped._clampedEndHour > clamped._clampedStartHour) {
            unavailMap['all'].push(clamped);
          }
        }
      });
    } else if (currentView === 'employee') {
      unavailabilities.forEach((u) => {
        if (u.local_start_date <= dayDateStr && u.local_end_date >= dayDateStr) {
          const key = u.employee_id;
          if (!unavailMap[key]) unavailMap[key] = [];
          const startsToday = u.local_start_date === dayDateStr;
          const endsToday = u.local_end_date === dayDateStr;
          const isMultiDay = u.local_start_date !== u.local_end_date;
          const clamped = {
            ...u,
            _clampedStartHour: startsToday ? Math.max(startHour, u.local_start_hour) : startHour,
            _clampedEndHour: endsToday ? Math.min(endHour, u.local_end_hour) : endHour,
            _isMultiDay: isMultiDay,
            _dayPosition: isMultiDay ? (startsToday ? 'first' : (endsToday ? 'last' : 'middle')) : undefined,
          };
          if (clamped._clampedEndHour > clamped._clampedStartHour) {
            unavailMap[key].push(clamped);
          }
        }
      });
    }

    return { eventsMap: evMap, unavailabilitiesMap: unavailMap };
  }, [filteredEvents, unavailabilities, currentView, isAllView, dayDateStr, startHour, endHour]);

  // Build calendar notes for this day
  const dayCalendarNotes = useMemo(() => {
    return calendarNotes.filter((n) => {
      return n.local_start_date <= dayDateStr && n.local_end_date >= dayDateStr;
    }).map((n) => {
      const startsToday = n.local_start_date === dayDateStr;
      const endsToday = n.local_end_date === dayDateStr;
      const isMultiDay = n.local_start_date !== n.local_end_date;
      return {
        ...n,
        _itemType: 'calendar_note',
        _clampedStartHour: startsToday ? Math.max(startHour, n.local_start_hour) : startHour,
        _clampedEndHour: endsToday ? Math.min(endHour, n.local_end_hour) : endHour,
        _isMultiDay: isMultiDay,
      };
    }).filter((n) => n._clampedEndHour > n._clampedStartHour);
  }, [calendarNotes, dayDateStr, startHour, endHour]);

  // Build booking events for this day
  const dayBookingEvents = useMemo(() => {
    if (!bookingEvents || bookingEvents.length === 0) return [];
    return bookingEvents.filter((b) => b.local_start_date === dayDateStr);
  }, [bookingEvents, dayDateStr]);

  // Build break period events for this day
  const dayBreakPeriodEvents = useMemo(() => {
    if (!breakPeriodEvents || breakPeriodEvents.length === 0) return [];
    return breakPeriodEvents.filter((b) => b.local_start_date === dayDateStr);
  }, [breakPeriodEvents, dayDateStr]);

  // O(1) lookups
  const getEventsForResource = (resourceId) => eventsMap[resourceId] || [];
  const getUnavailabilitiesForResource = (resourceId) => unavailabilitiesMap[resourceId] || [];

  // Sidebar drag handlers
  const handleBodyScroll = useCallback((e) => {
    if (headerRef.current) {
      headerRef.current.scrollLeft = e.target.scrollLeft;
    }
  }, []);

  const handleDragOver = useCallback((e, resourceId) => {
    if (!draggedMaintenance) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const hour = (y / HOUR_HEIGHT) + startHour;
    const snappedHour = Math.round(hour * SNAPS_PER_HOUR) / SNAPS_PER_HOUR;

    setDropHighlight({ resourceId, hour: snappedHour });
  }, [draggedMaintenance, startHour]);

  const handleDragLeave = useCallback(() => {
    setDropHighlight(null);
  }, []);

  const handleDrop = useCallback(async (e, resourceId) => {
    e.preventDefault();
    setDropHighlight(null);
    if (!canSchedule) return;
    if (!draggedMaintenance) return;

    const droppedMaintenance = draggedMaintenance;
    setDraggedMaintenance(null);

    try {
      const rect = e.currentTarget.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const hour = (y / HOUR_HEIGHT) + startHour;
      const snappedHour = Math.round(hour * SNAPS_PER_HOUR) / SNAPS_PER_HOUR;

      const startTime = setHour(date, Math.floor(snappedHour), Math.round((snappedHour % 1) * 60));
      const endTime = setHour(date, Math.floor(snappedHour + 1), Math.round(((snappedHour + 1) % 1) * 60));

      const startTimeStr = format(startTime, 'yyyy-MM-ddTHH:mm:ss');
      const endTimeStr = format(endTime, 'yyyy-MM-ddTHH:mm:ss');

      const maintenanceId = droppedMaintenance.id;
      const scheduleData = {
        maintenance: maintenanceId,
        start_time: startTimeStr,
        end_time: endTimeStr,
      };

      if (currentView === 'bay') {
        scheduleData.service_bay = resourceId;
      } else if (currentView === 'employee' && !isAllView) {
        // The column it was dropped on is this slot's team
        scheduleData.employee_ids = [resourceId];
      }

      const response = await axios.post('/api/scheduling/schedules/', scheduleData);
      const newSchedule = response.data;

      const eventEmployeeIds = newSchedule.employee_ids || droppedMaintenance.employee_ids || [];

      addEvent({
        id: newSchedule.id,
        maintenance_id: maintenanceId,
        start_time: startTimeStr,
        end_time: endTimeStr,
        vehicle_registration: droppedMaintenance.vehicle_registration,
        description: droppedMaintenance.description,
        customer_name: droppedMaintenance.customer_name,
        state: 'planned',
        service_bay_id: currentView === 'bay' ? resourceId : null,
        employee_ids: eventEmployeeIds,
      });

      removeFromUnscheduled(droppedMaintenance.id);
    } catch (err) {
      console.error('Failed to schedule maintenance:', err);
      const data = err.response?.data;
      const message = data?.detail || data?.message
        || (data?.maintenance && (Array.isArray(data.maintenance) ? data.maintenance[0] : data.maintenance))
        || t('Failed to schedule maintenance');
      setDropError(message);
    }
  }, [draggedMaintenance, date, startHour, currentView, isAllView, addEvent, removeFromUnscheduled, setDraggedMaintenance, t]);

  // DragSelection callbacks
  const handleDragEnd = useCallback(async (selection) => {
    if (!canSchedule) return;
    // Scheduling mode: directly create schedule, no context menu
    const { schedulingModeTarget, exitSchedulingMode, clearDragSelection } = useCalendarState.getState();
    if (schedulingModeTarget) {
      try {
        const actualStart = Math.min(selection.startHour, selection.endHour);
        const actualEnd = Math.max(selection.startHour, selection.endHour);
        const startTime = setHour(date, Math.floor(actualStart), Math.round((actualStart % 1) * 60));
        const endTime = setHour(date, Math.floor(actualEnd), Math.round((actualEnd % 1) * 60));
        const startTimeStr = format(startTime, 'yyyy-MM-ddTHH:mm:ss');
        const endTimeStr = format(endTime, 'yyyy-MM-ddTHH:mm:ss');

        const scheduleData = {
          maintenance: schedulingModeTarget.id,
          start_time: startTimeStr,
          end_time: endTimeStr,
        };

        if (currentView === 'bay') {
          scheduleData.service_bay = selection.resourceId;
        } else if (currentView === 'employee') {
          // The column it was drawn in is this slot's team
          scheduleData.employee_ids = [selection.resourceId];
        }

        const response = await axios.post('/api/scheduling/schedules/', scheduleData);

        const eventEmployeeIds = response.data.employee_ids || schedulingModeTarget.employee_ids || [];

        addEvent({
          id: response.data.id,
          maintenance_id: schedulingModeTarget.id,
          start_time: startTimeStr,
          end_time: endTimeStr,
          vehicle_registration: schedulingModeTarget.vehicle_registration || '',
          description: schedulingModeTarget.description || '',
          customer_name: schedulingModeTarget.customer_name || null,
          state: 'planned',
          service_bay_id: currentView === 'bay' ? selection.resourceId : null,
          employee_ids: eventEmployeeIds,
        });

        removeFromUnscheduled(schedulingModeTarget.id);
        exitSchedulingMode();
        clearDragSelection();
      } catch (err) {
        const data = err.response?.data;
        const message = data?.detail || data?.message || t('Failed to schedule maintenance');
        setDropError(message);
        clearDragSelection();
      }
      return;
    }

    // Normal flow: open context menu
    useCalendarState.setState({
      contextMenuOpen: true,
      eventContextMenu: null,
      contextMenuPosition: selection.position,
      selectedTimeSlot: {
        resourceId: selection.resourceId,
        resourceType: currentView,
        dayIndex: 0,
        date: date,
        startHour: selection.startHour,
        endHour: selection.endHour,
        startDayIndex: 0,
        endDayIndex: 0,
        startDate: date,
        endDate: date,
        isMultiDay: false,
      },
    });
  }, [date, currentView, addEvent, removeFromUnscheduled, t]);

  const handleDragCancel = useCallback(() => {
    useCalendarState.setState({
      contextMenuOpen: false,
      selectedTimeSlot: null,
    });
  }, []);

  // Current time indicator
  const now = new Date();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  const currentTimePosition = (currentHour - startHour + currentMinute / 60) * HOUR_HEIGHT;

  // Empty state
  if (resources.length === 0) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          p: 4,
        }}
      >
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          {currentView === 'employee' || isAllView ? t('No employees yet') : t('No service bays yet')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {currentView === 'employee' || isAllView
            ? t('Add employees to see them in the calendar')
            : t('Add service bays to see them in the calendar')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={gridRef}
      sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      <DragSelection
        gridRef={gridRef}
        workStartHour={startHour}
        workEndHour={endHour}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      />

      {/* Header Row: Time + Resource Columns */}
      <Box
        ref={headerRef}
        sx={{
          display: 'flex',
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          flexShrink: 0,
          overflow: 'hidden',
        }}
      >
        {/* Time Header */}
        <Box
          sx={{
            width: TIME_COLUMN_WIDTH,
            minWidth: TIME_COLUMN_WIDTH,
            py: isShortScreen ? 0.5 : 1.5,
            px: 1,
            borderRight: 1,
            borderColor: 'divider',
            fontSize: '11px',
            fontWeight: 600,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            textAlign: 'center',
          }}
        >
          {t('Time')}
        </Box>

        {/* Resource Headers (columns) */}
        {resources.map((resource) => (
          <Box
            key={resource.id}
            onClick={currentView === 'employee' ? () => {
              setSelectedEmployee(resource);
              setEmployeeDialogOpen(true);
            } : undefined}
            sx={{
              flex: 1,
              minWidth: MIN_RESOURCE_COLUMN_WIDTH,
              py: isShortScreen ? 0.75 : 1.25,
              px: 1.5,
              borderRight: 1,
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              ...(currentView === 'employee' && {
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
              }),
            }}
          >
            {/* Avatar / Badge */}
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: currentView === 'bay' ? '8px' : '50%',
                bgcolor: resource.color || 'secondary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '11px',
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {resource.initials || resource.id}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {resource.name}
              </Typography>
              <Typography sx={{ fontSize: '11px', color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {resource.title || (resource.role ? t(`role_${resource.role}`) : '') || resource.description || ''}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Scrollable Body */}
      <Box ref={scrollBodyRef} onScroll={handleBodyScroll} sx={{ flex: 1, overflow: 'auto', display: 'flex' }}>
        {/* Fixed Time Column: as tall as the rows, not stretched to the view (a short day's grid
            ends at its closing time) */}
        <Box
          sx={{
            width: TIME_COLUMN_WIDTH,
            minWidth: TIME_COLUMN_WIDTH,
            flexShrink: 0,
            alignSelf: 'flex-start',
            bgcolor: 'background.paper',
            borderRight: 1,
            borderColor: 'divider',
            position: 'sticky',
            left: 0,
            zIndex: 10,
          }}
        >
          <Box sx={{ height: rowHeight, position: 'relative' }}>
            {hours.map((hour) => (
              <Box
                key={hour}
                sx={{
                  height: HOUR_HEIGHT,
                  borderBottom: 1,
                  borderColor: UI_COLORS.hourLine,
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  pt: 0.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '11px',
                    fontWeight: 500,
                    color: 'text.secondary',
                  }}
                >
                  {hour}:00
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Resource Columns */}
        {resources.map((resource) => {
          const resourceEvents = getEventsForResource(resource.id);
          const resourceUnavailabilities = getUnavailabilitiesForResource(resource.id);

          // Combine and calculate overlaps.
          // The 'all' view collects every note; an employee column shows the notes pinned to
          // that mechanic. Notes without an employee belong to the whole workshop, so they
          // stay in the 'all' view rather than being repeated in every column.
          const maintenancesWithType = resourceEvents.map((e) => ({ ...e, _itemType: 'maintenance' }));
          const unavailabilitiesWithType = resourceUnavailabilities.map((u) => ({ ...u, _itemType: 'unavailability' }));
          const notesWithType = isAllView
            ? dayCalendarNotes.map((n) => ({ ...n }))
            : currentView === 'employee'
              ? dayCalendarNotes.filter((n) => n.employee_id === resource.id).map((n) => ({ ...n }))
              : [];
          const allItems = [...maintenancesWithType, ...unavailabilitiesWithType, ...notesWithType];
          const itemsWithOverlaps = calculateEventOverlaps(allItems, startHour, parseISO);

          return (
            <Box
              key={resource.id}
              data-resource-id={resource.id}
              data-day-column={0}
              data-day-date={dayDateStr}
              sx={{
                flex: 1,
                minWidth: MIN_RESOURCE_COLUMN_WIDTH,
                borderRight: 1,
                borderColor: 'divider',
                position: 'relative',
                height: rowHeight,
                bgcolor: isTodayView ? UI_COLORS.todayBg : 'transparent',
                cursor: draggedMaintenance ? 'copy' : 'crosshair',
                overflow: 'visible',
              }}
              onDragOver={(e) => handleDragOver(e, resource.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, resource.id)}
            >
              {/* Hour Grid Lines */}
              {hours.map((hour) => (
                <Box
                  key={hour}
                  sx={{
                    height: HOUR_HEIGHT,
                    borderBottom: 1,
                    borderColor: UI_COLORS.hourLine,
                    position: 'relative',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: 0,
                      right: 0,
                      height: '1px',
                      bgcolor: UI_COLORS.halfHourLine,
                    }}
                  />
                </Box>
              ))}

              {/* Outside this day's work hours: the employee's own shift in the employee
                  view, the workshop's hours of that weekday in the bay view */}
              <NonWorkingHoursOverlay
                hours={currentView === 'employee'
                  ? (resource.work_hours ? hoursForDate(resource.work_hours, date) : undefined)
                  : hoursForDate(workSchedule.days, date)}
                startHour={startHour}
                endHour={endHour}
                hourHeight={HOUR_HEIGHT}
              />

              {/* Current Time Line */}
              {isTodayView && currentTimePosition > 0 && currentTimePosition < rowHeight && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: currentTimePosition,
                    left: 0,
                    right: 0,
                    zIndex: 5,
                    // Drawn above the cards but never takes the click (short cards are mostly line)
                    pointerEvents: 'none',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: UI_COLORS.currentTimeLine,
                      ml: '-4px',
                    }}
                  />
                  <Box sx={{ flex: 1, height: 2, bgcolor: UI_COLORS.currentTimeLine }} />
                </Box>
              )}

              {/* Drop Highlight for sidebar drag */}
              {dropHighlight && dropHighlight.resourceId === resource.id && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: 4,
                    right: 4,
                    top: (dropHighlight.hour - startHour) * HOUR_HEIGHT + 2,
                    height: HOUR_HEIGHT - 4,
                    bgcolor: 'rgba(59, 130, 246, 0.2)',
                    border: '2px dashed #3b82f6',
                    borderRadius: '8px',
                    pointerEvents: 'none',
                    zIndex: 50,
                  }}
                />
              )}

              {/* Events & Unavailabilities */}
              {itemsWithOverlaps.map((item) => {
                if (item._itemType === 'unavailability') {
                  return (
                    <UnavailabilityEvent
                      key={`unavail-${item.id}`}
                      unavailability={item}
                      startHour={startHour}
                      endHour={endHour}
                      dayIndex={0}
                      overlapIndex={item.overlapIndex}
                      overlapTotal={item.overlapTotal}
                      employees={isAllView ? employees : undefined}
                      showEmployeeName={isAllView}
                    />
                  );
                }
                if (item._itemType === 'calendar_note') {
                  return (
                    <CalendarNoteEvent
                      key={`note-${item.id}`}
                      note={item}
                      startHour={startHour}
                      endHour={endHour}
                      dayIndex={0}
                      overlapIndex={item.overlapIndex}
                      overlapTotal={item.overlapTotal}
                    />
                  );
                }
                if (isAllView) {
                  return (
                    <AllViewEvent
                      key={item._isMultiDay ? `${item.id}-day` : item.id}
                      event={item}
                      startHour={startHour}
                      endHour={endHour}
                      dayIndex={0}
                      overlapIndex={item.overlapIndex}
                      overlapTotal={item.overlapTotal}
                      employees={employees}
                      serviceBays={serviceBays}
                    />
                  );
                }
                return (
                  <MaintenanceEvent
                    key={item._isMultiDay ? `${item.id}-day` : item.id}
                    event={item}
                    startHour={startHour}
                    endHour={endHour}
                    dayIndex={0}
                    overlapIndex={item.overlapIndex}
                    overlapTotal={item.overlapTotal}
                    resourceId={resource.id}
                  />
                );
              })}

              {/* Break period overlays */}
              {dayBreakPeriodEvents.map((breakPeriod) => {
                const topHour = breakPeriod.local_start_hour - startHour;
                const durationHours = breakPeriod.local_end_hour - breakPeriod.local_start_hour;
                if (topHour < 0 || durationHours <= 0) return null;

                return (
                  <Box
                    key={breakPeriod.id}
                    sx={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      top: topHour * HOUR_HEIGHT,
                      height: durationHours * HOUR_HEIGHT,
                      bgcolor: BREAK_PERIOD_CONFIG.bg,
                      borderTop: `1px dashed ${BREAK_PERIOD_CONFIG.color}`,
                      borderBottom: `1px dashed ${BREAK_PERIOD_CONFIG.color}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      zIndex: 1,
                      '&:hover': {
                        bgcolor: 'rgba(148, 163, 184, 0.2)',
                      },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      window.location.href = '/settings?tab=booking';
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '9px',
                        fontWeight: 500,
                        color: BREAK_PERIOD_CONFIG.text,
                        opacity: 0.7,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        userSelect: 'none',
                      }}
                    >
                      {breakPeriod.name || t('booking.calendar.break_period')}
                    </Typography>
                  </Box>
                );
              })}

              {/* Booking events overlay */}
              {dayBookingEvents.map((booking) => {
                const bookingConfig = BOOKING_STATUS_CONFIG[booking.status] || BOOKING_STATUS_CONFIG.pending;
                const topHour = booking.local_start_hour - startHour;
                const durationHours = booking.local_end_hour - booking.local_start_hour;
                if (topHour < 0 || durationHours <= 0) return null;

                return (
                  <Box
                    key={booking.id}
                    sx={{
                      position: 'absolute',
                      left: 4,
                      right: 4,
                      top: topHour * HOUR_HEIGHT + 2,
                      height: durationHours * HOUR_HEIGHT - 4,
                      bgcolor: bookingConfig.bg,
                      border: `2px ${bookingConfig.borderStyle} ${bookingConfig.color}`,
                      borderRadius: '6px',
                      px: 0.75,
                      py: 0.25,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      zIndex: 3,
                      opacity: 0.9,
                      '&:hover': { opacity: 1, zIndex: 10 },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      window.location.href = '/bookings';
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '10px',
                        fontWeight: 600,
                        color: bookingConfig.text,
                        lineHeight: 1.2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {booking.service_name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '9px',
                        color: bookingConfig.text,
                        opacity: 0.8,
                        lineHeight: 1.2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {booking.customer_name} {booking.vehicle_registration && `\u00B7 ${booking.vehicle_registration}`}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          );
        })}

        {/* Calendar notes in 'all' view - show in first column only */}
        {/* (Notes are not resource-specific, they are already rendered in non-all views above) */}
      </Box>

      {/* Error Snackbar */}
      <Snackbar
        open={!!dropError}
        autoHideDuration={5000}
        onClose={() => setDropError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setDropError(null)} severity="error" sx={{ width: '100%' }}>
          {dropError}
        </Alert>
      </Snackbar>

      {/* Employee Details Dialog */}
      {selectedEmployee && (
        <EmployeeDetailsDialog
          open={employeeDialogOpen}
          onClose={() => {
            setEmployeeDialogOpen(false);
            axios.get(`/api/employees/${selectedEmployee.id}/`).then((res) => {
              const emp = res.data;
              updateEmployee(selectedEmployee.id, {
                work_day_start: emp.effective_work_day_start || null,
                work_day_end: emp.effective_work_day_end || null,
                working_weekdays: emp.effective_working_weekdays,
                work_hours: emp.effective_work_hours,
                name: emp.name,
                title: emp.title,
              });
            }).catch(() => {});
            setSelectedEmployee(null);
          }}
          employee={selectedEmployee}
        />
      )}
    </Box>
  );
}

export default withTranslation()(CalendarDayGrid);
